import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getNotifications, markNotificationRead as apiMarkRead, markAllNotificationsRead as apiMarkAllRead } from '@/services/notificationsService';

/** Notification types from backend (API may send these exact strings). */
export const NOTIFICATION_TYPES = {
    REMINDER: 'reminder',
    SHIFT_RESPONSE: 'shift_response',
    SHIFT_RESPONSE_ACCEPTED: 'shift_response_accepted',
    SHIFT_RESPONSE_DENIED: 'shift_response_denied',
    SHIFT_CANCELLATION: 'shift_cancellation',
    QUOTE_ACCEPTED: 'quote_accepted',
    SUBHIRE_ACCEPTED: 'subhire_accepted'
};

/** Map backend type to UI type. variant is set separately for shift responses (accepted=green, denied=red, mixed=amber). */
const backendTypeToUiType = {
    [NOTIFICATION_TYPES.REMINDER]: 'reminder',
    [NOTIFICATION_TYPES.SHIFT_RESPONSE]: 'freelancer_shift_acceptance',
    [NOTIFICATION_TYPES.SHIFT_RESPONSE_ACCEPTED]: 'freelancer_shift_acceptance',
    [NOTIFICATION_TYPES.SHIFT_RESPONSE_DENIED]: 'freelancer_shift_acceptance',
    [NOTIFICATION_TYPES.SHIFT_CANCELLATION]: 'freelancer_shift_cancellation',
    'shift_canceled': 'freelancer_shift_cancellation',
    [NOTIFICATION_TYPES.QUOTE_ACCEPTED]: 'quote_acceptance',
    [NOTIFICATION_TYPES.SUBHIRE_ACCEPTED]: 'subhire_acceptance'
};

/** Build shift description from notification data (legacy/fallback when accepted_count/denied_count not used). */
function getShiftDescription(data) {
    if (data.shift_description && String(data.shift_description).trim()) return data.shift_description.trim();
    const labels = data.shift_labels;
    if (Array.isArray(labels) && labels.length > 0) {
        const list = labels.map((l) => (l && String(l).trim()) || '').filter(Boolean);
        if (list.length > 0) return list.join(', ');
    }
    const assignmentName = data.assignment_name && String(data.assignment_name).trim();
    const date = data.start_day_date && String(data.start_day_date).trim();
    if (assignmentName && date) return `${assignmentName} (${date})`;
    if (assignmentName) return assignmentName;
    const count = data.slot_count ?? data.slots_count;
    if (count != null && Number(count) > 0) return Number(count) === 1 ? '1 shift' : `${Number(count)} shifts`;
    return null;
}

/** Pick first non-empty string from possible freelancer name keys (API may send snake_case or nested). */
function getFreelancerName(data) {
    const raw =
        data.freelancer_name ??
        data.freelancerName ??
        data.freelancer?.name ??
        data.user?.name ??
        '';
    const s = raw && String(raw).trim();
    return s || 'A freelancer';
}

/**
 * Build subtitle for shift response using backend data.
 * acceptedOverride: when API sends shift_response_accepted / shift_response_denied we pass true/false; for mixed (shift_response) we pass null.
 */
function getShiftResponseSubtitle(data, fallback, acceptedOverride = null) {
    const name = getFreelancerName(data);
    const projectName = data.project_name && String(data.project_name).trim();
    const onProject = projectName ? ` on ${projectName}` : '';
    const acceptedCount = data.accepted_count != null ? Number(data.accepted_count) : null;
    const deniedCount = data.denied_count != null ? Number(data.denied_count) : null;
    const accepted = acceptedOverride !== null ? acceptedOverride : (data.accepted === true);
    if (acceptedOverride === null && acceptedCount != null && deniedCount != null && (acceptedCount > 0 && deniedCount > 0)) {
        return `${name} responded: ${acceptedCount} accepted, ${deniedCount} denied${onProject}`;
    }
    if (accepted && acceptedCount != null && acceptedCount > 0) {
        const shiftWord = acceptedCount === 1 ? 'shift' : 'shifts';
        return `${name} has accepted ${acceptedCount} ${shiftWord}${onProject}`;
    }
    if (!accepted && deniedCount != null && deniedCount > 0) {
        const shiftWord = deniedCount === 1 ? 'shift' : 'shifts';
        return `${name} has denied ${deniedCount} ${shiftWord}${onProject}`;
    }
    const action = accepted ? 'accepted' : 'denied';
    const shiftPart = getShiftDescription(data) || 'shift(s)';
    return `${name} has ${action} ${shiftPart}${onProject}`.trim() || fallback;
}

/** Build a specific subtitle for quote_accepted when data has quote_ref, quote_title, client_name, or project_name. */
function getQuoteSubtitle(data, fallback) {
    const ref = data.quote_ref ?? data.quote_id;
    const title = data.quote_title ?? data.title;
    const client = data.client_name ?? data.company_name;
    const project = data.project_name;
    const parts = [];
    if (ref && title) parts.push(`Quote ${ref}: ${title}`);
    else if (title) parts.push(title);
    else if (ref) parts.push(`Quote ${ref}`);
    if (client) parts.push(client);
    if (project) parts.push(project);
    return parts.length > 0 ? parts.join(' · ') : fallback;
}

/** Build a specific subtitle for subhire_accepted when data has subhire ref, description, project_name. */
function getSubhireSubtitle(data, fallback) {
    const ref = data.subhire_ref ?? data.subhire_id;
    const desc = data.subhire_description ?? data.description;
    const project = data.project_name;
    const parts = [];
    if (ref && desc) parts.push(`${ref}: ${desc}`);
    else if (desc) parts.push(desc);
    else if (ref) parts.push(`Subhire ${ref}`);
    if (project) parts.push(project);
    return parts.length > 0 ? parts.join(' · ') : fallback;
}

/** Map API notification to UI shape. variant: 'accepted' | 'denied' | 'mixed' for shift (green / red / amber). */
function mapNotificationFromApi(n) {
    if (!n) return null;
    const data = n.data || {};
    const projectId = data.project_id != null ? String(data.project_id) : null;
    let link = null;
    let subtitle = n.body || n.title || '';
    let title = n.title || 'Notification';
    let variant = null;
    const rawType = String(n.type || '').toLowerCase().trim().replace(/\s+/g, '_');
    if (rawType === NOTIFICATION_TYPES.SHIFT_RESPONSE_DENIED && projectId) {
        link = `/project/${projectId}`;
        variant = 'denied';
        subtitle = getShiftResponseSubtitle(data, subtitle, false);
        title = n.title || 'Shift denied';
    } else if (rawType === NOTIFICATION_TYPES.SHIFT_RESPONSE_ACCEPTED && projectId) {
        link = `/project/${projectId}`;
        variant = 'accepted';
        subtitle = getShiftResponseSubtitle(data, subtitle, true);
        title = n.title || 'Shift accepted';
    } else if (rawType === NOTIFICATION_TYPES.SHIFT_RESPONSE && projectId) {
        link = `/project/${projectId}`;
        variant = 'mixed';
        subtitle = getShiftResponseSubtitle(data, subtitle, null);
        title = n.title || 'Shift response';
    } else if ((rawType === NOTIFICATION_TYPES.SHIFT_CANCELLATION || rawType === 'shift_canceled') && projectId) {
        link = `/project/${projectId}`;
        const shiftPart = getShiftDescription(data) || 'shift(s)';
        subtitle = subtitle || `Shift cancellation: ${shiftPart}`;
        title = title === 'Notification' ? 'Shift cancelled' : title;
    } else if (rawType === NOTIFICATION_TYPES.SUBHIRE_ACCEPTED && projectId) {
        link = `/project/${projectId}/subhires`;
        subtitle = getSubhireSubtitle(data, subtitle || 'A subhire has been accepted.');
        title = title === 'Notification' ? 'Subhire accepted' : title;
    } else if (rawType === NOTIFICATION_TYPES.QUOTE_ACCEPTED) {
        link = '/finance/quotes';
        subtitle = getQuoteSubtitle(data, subtitle || 'A quote has been accepted.');
        title = title === 'Notification' ? 'Quote accepted' : title;
    } else if (rawType === NOTIFICATION_TYPES.REMINDER) {
        if (projectId) link = `/project/${projectId}/reminders`;
        subtitle = subtitle || (data.message && String(data.message).trim()) || (n.body && String(n.body).trim()) || 'A reminder is due.';
        title = title === 'Notification' ? 'Reminder' : title;
    }
    const uiType = backendTypeToUiType[rawType] || backendTypeToUiType[n.type] || n.type;
    return {
        id: n.id,
        type: uiType,
        variant,
        read: !!n.read_at,
        title,
        subtitle,
        link,
        linkLabel: link ? 'View' : null,
        projectId,
        createdAt: n.created_at || n.createdAt
    };
}

/** Exported so real-time/polling can map a single raw notification and show a toast. */
export { mapNotificationFromApi };

export const useNotificationsStore = defineStore('notifications', () => {
    const items = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const unreadCount = computed(() => items.value.filter((n) => !n.read).length);

    const list = computed(() => [...items.value].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));

    /** Notifications for a given project. */
    const listForProject = (projectId) => {
        if (projectId == null || projectId === '') return [];
        const id = String(projectId);
        return [...items.value]
            .filter((n) => n.projectId != null && String(n.projectId) === id)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    async function loadNotifications(params = {}) {
        loading.value = true;
        error.value = null;
        try {
            const { list: rawList } = await getNotifications({ page: 1, per_page: 50, ...params });
            const mapped = (rawList || []).map(mapNotificationFromApi).filter(Boolean);
            items.value = mapped;
        } catch (err) {
            console.error('[notificationsStore] loadNotifications failed:', err);
            error.value = err?.message ?? 'Failed to load notifications';
            items.value = [];
        } finally {
            loading.value = false;
        }
    }

    async function markAsRead(id) {
        const n = items.value.find((item) => item.id === id);
        if (n) n.read = true;
        try {
            await apiMarkRead(id);
        } catch (err) {
            if (n) n.read = false;
            console.error('[notificationsStore] markAsRead failed:', err);
        }
    }

    async function markAllAsRead() {
        items.value.forEach((n) => { n.read = true; });
        try {
            await apiMarkAllRead();
        } catch (err) {
            items.value.forEach((n) => { n.read = false; });
            console.error('[notificationsStore] markAllAsRead failed:', err);
        }
    }

    /** Add a single mapped notification to the list (e.g. from real-time/polling). Dedupes by id. */
    function prependNotification(mapped) {
        if (!mapped?.id) return;
        const exists = items.value.some((n) => String(n.id) === String(mapped.id));
        if (exists) return;
        items.value = [mapped, ...items.value];
    }

    return {
        items,
        loading,
        error,
        unreadCount,
        list,
        listForProject,
        markAsRead,
        markAllAsRead,
        loadNotifications,
        prependNotification
    };
});
